import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { TranslaterService } from './translater.service';
import { BaseApi } from 'src/backend/api/base-api';
import { map } from 'rxjs/operators';

// Snapshot consumers assign locally so their templates can keep using the
// canonical dual-fallback pattern: {{ langObj ? langObj?.X : ('X' | translate) }}.
export interface LangSnapshot {
  langObj: any;
  languageDir: 'ltr' | 'rtl';
  pageId: any;
  moduleId: any;
}


@Injectable({ providedIn: 'root' })
export class LangLoaderService {

    // Stream of plain () events fired whenever @ngx-translate emits onLangChange.
    // Consumers subscribe to know when to re-invoke loadForCurrentMenu().
    readonly onLangChange$: Observable<void>;

    constructor(
        private baseApi: BaseApi,
        private translater: TranslaterService,
        private translate: TranslateService,
    ) {
        this.onLangChange$ = this.translate.onLangChange.pipe(map(() => undefined));
    }

    loadForCurrentMenu(): Observable<LangSnapshot> {
        const pageId   = this.baseApi.getCurrentMenuID();
        const moduleId = this.baseApi.getUserPermissionsSession()
            .find((x: any) => x.menuId == pageId)?.parentId;
        return this.loadFor(pageId, moduleId);
    }

    // Same shape as loadForCurrentMenu but takes explicit pageId/moduleId.
    // Used by pages that don't have their own menu entry (e.g. the admin-access
    // detail page borrows the grid's ids) so they can pass them directly
    // instead of relying on CURRENT_MENU_ID, which may not match.
    loadFor(pageId: any, moduleId: any): Observable<LangSnapshot> {
        const languageDir: 'ltr' | 'rtl' = this.translate.currentLang === 'ar' ? 'rtl' : 'ltr';
        const key = {
            ModuleId: moduleId,
            PageId: pageId,
            LanguageId: this.languageIdFromCurrent(),
        };
        return this.translater.getLangWords(key).pipe(
            map((res: any) => {
                const obj: any = {};
                (res?.tagList ?? []).forEach((el: any) =>
                    obj[el.tagName] = el.translatedValue);
                return { langObj: obj, languageDir, pageId, moduleId } as LangSnapshot;
            }),
        );
    }

    // Dictionary lookup with English-key fallback. The | translate pipe handles
    // the *first* paint (before getLangWords resolves) inside templates; this
    // method handles the TS-side case where a Swal/toaster needs a string at
    // call time.
    t(langObj: any, key: string, fallback?: string): string {
        if (langObj && langObj[key]) return langObj[key];
        return fallback ?? key;
    }

    // {0}/{1}/{N} -> values[N] replacement, returns a flat string.
    // Used by toaster/Swal text that needs a single rendered string.
    format(template: string, values: string[]): string {
        return (template ?? '').replace(/\{(\d+)\}/g,
            (_m, i) => values[parseInt(i, 10)] ?? '');
    }

    // Same placeholder format but returns alternating { text, isData } chunks
    // so the detail page's banners can wrap the data portions in <strong>.
    // Example:
    //   formatChunks('Waiting for {0} to respond.', ['Alice'])
    //   -> [ { text: 'Waiting for ', isData: false },
    //        { text: 'Alice',        isData: true  },
    //        { text: ' to respond.', isData: false } ]
    formatChunks(template: string, values: string[]): { text: string; isData: boolean }[] {
        const out: { text: string; isData: boolean }[] = [];
        const tpl = template ?? '';
        const re = /\{(\d+)\}/g;
        let lastIndex = 0;
        let m: RegExpExecArray | null;
        while ((m = re.exec(tpl)) !== null) {
            if (m.index > lastIndex) {
                out.push({ text: tpl.slice(lastIndex, m.index), isData: false });
            }
            const idx = parseInt(m[1], 10);
            out.push({ text: String(values[idx] ?? ''), isData: true });
            lastIndex = re.lastIndex;
        }
        if (lastIndex < tpl.length) {
            out.push({ text: tpl.slice(lastIndex), isData: false });
        }
        return out;
    }

    private languageIdFromCurrent(): number {
        switch (this.translate.currentLang) {
            case 'ar': return 2;
            case 'fr': return 3;
            case 'sp': return 4;
            case 'gr': return 5;
            case 'du': return 6;
            default:   return 1;
        }
    }
}
